import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';

/**
 * Route guard for pages that require a logged-in session.
 *
 * Used as a wrapper element in App.jsx:
 *   <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
 *
 * While auth is loading (first paint before /api/me resolves), we show a
 * lightweight placeholder. Avoids the "flash of logged-out state" where
 * the page briefly redirects to /login and then bounces back.
 */
export function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingShell />;
  }

  if (!isAuthenticated) {
    // Preserve the original destination so LoginPage can redirect back
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function LoadingShell() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--paper)',
      }}
      role="status"
      aria-live="polite"
    >
      <div
        aria-hidden="true"
        style={{
          width: 40, height: 40, borderRadius: '50%',
          border: '3px solid var(--line)',
          borderTopColor: 'var(--tocyn-green)',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <span className="sr-only">Loading…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
